import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { HOME_SECTIONS } from '../constants/homeSections';
import './PermissionEditor.css';

const AVAILABLE_CARDS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'visitor-entries', label: 'Visitor Entries' },
    { id: 'task-management', label: 'Task Management' },
    { id: 'monthly-documents', label: 'Daily Documents' },
    { id: 'cheque-register', label: 'Cheque Register' },
    { id: 'cashbook', label: 'Cashbook' },
    { id: 'document-log-book', label: 'Document Log Book' },
    { id: 'clearance-form', label: 'Clearance Form' },
    { id: 'register-book', label: 'Register Book' },
    { id: 'user-management', label: 'User Management' },
    { id: 'sales-target-management', label: 'Sales Target Management' },
    { id: 'legal-cases', label: 'Legal Cases' },
    { id: 'invoice-management', label: 'Invoice Management' },
    { id: 'aging-reports', label: 'Aging Reports' },
    { id: 'payslip-distribution', label: 'Payslip Distribution' },
    { id: 'stock-reorder', label: 'Stock Reorder' },
    { id: 'delivery-management', label: 'Delivery Management' },
    { id: 'customer-register', label: 'Customer Register' },
    { id: 'vision', label: 'Strategic Vision' },
    { id: 'financial-performance', label: 'Financial Performance' },
    { id: 'weekly-meetings', label: 'Weekly Meetings' },
    { id: 'subscription-payments', label: 'Subscription Payments' },
    { id: 'file-manager', label: 'File Manager' },
    { id: 'gp-report', label: 'Sales Analysis' },
    { id: 'action-log', label: 'Action Log' },
    { id: 'working-time', label: 'Working Time' },
    { id: 'frozen-pick-portal', label: 'Frozen Picking' }
];

const AVAILABLE_ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];

const PermissionEditor = ({ user, onClose, onUpdate }) => {
    const { user: currentUser } = useAuth();
    const [permissions, setPermissions] = useState({
        cards: [],
        actions: [],
        cardActions: {},
        // Stored as the HIDDEN list: a user with nothing configured sees every
        // division, and a division added later is visible by default rather
        // than silently hidden for everyone.
        hiddenHomeSections: []
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user?.permissions) {
            setPermissions({
                cards: user.permissions.cards || [],
                actions: user.permissions.actions || [],
                cardActions: user.permissions.cardActions || {},
                hiddenHomeSections: user.permissions.hiddenHomeSections || []
            });
        }
    }, [user]);

    const handleCardToggle = (cardId) => {
        setPermissions(prev => {
            const newCards = prev.cards.includes(cardId)
                ? prev.cards.filter(c => c !== cardId)
                : [...prev.cards, cardId];
            
            // Remove card-specific actions if card is removed
            const newCardActions = { ...prev.cardActions };
            if (!newCards.includes(cardId)) {
                delete newCardActions[cardId];
            }
            
            return { ...prev, cards: newCards, cardActions: newCardActions };
        });
    };

    const handleCardActionToggle = (cardId, action) => {
        setPermissions(prev => {
            const cardActions = prev.cardActions[cardId] || [];
            const newCardActions = cardActions.includes(action)
                ? cardActions.filter(a => a !== action)
                : [...cardActions, action];
            
            return {
                ...prev,
                cardActions: {
                    ...prev.cardActions,
                    [cardId]: newCardActions
                }
            };
        });
    };

    // The checkbox reads as "show this division", so ticking it REMOVES the id
    // from the stored hidden list.
    const handleHomeSectionToggle = (sectionId) => {
        setPermissions(prev => {
            const hidden = prev.hiddenHomeSections || [];
            return {
                ...prev,
                hiddenHomeSections: hidden.includes(sectionId)
                    ? hidden.filter(s => s !== sectionId)
                    : [...hidden, sectionId]
            };
        });
    };

    const handleGlobalActionToggle = (action) => {
        setPermissions(prev => {
            const newActions = prev.actions.includes(action)
                ? prev.actions.filter(a => a !== action)
                : [...prev.actions, action];
            
            return { ...prev, actions: newActions };
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.patch(
                `/api/users/${user._id}/permissions`,
                permissions
            );

            if (response.data) {
                alert('Permissions updated successfully');
                onUpdate();
                onClose();
            }
        } catch (error) {
            console.error('Error updating permissions:', error);
            alert('Failed to update permissions');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content permission-editor-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Permissions: {user?.username}</h2>
                
                <div className="permission-section">
                    <h3>Global Actions (Fallback)</h3>
                    <p className="help-text">These actions apply to all cards unless card-specific actions are defined</p>
                    <div className="action-checkboxes">
                        {AVAILABLE_ACTIONS.map(action => (
                            <label key={action} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={permissions.actions.includes(action)}
                                    onChange={() => handleGlobalActionToggle(action)}
                                />
                                {action}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="permission-section">
                    <h3>Home Page Sections</h3>
                    <p className="help-text">
                        Which tiles this user sees on the home page. Unticking one only
                        removes the tile from that screen &mdash; it does not change what the
                        user may open, which stays governed by the card permissions below.
                    </p>
                    <div className="home-section-checkboxes">
                        {HOME_SECTIONS.map(section => (
                            <label key={section.id} className="home-section-checkbox">
                                <input
                                    type="checkbox"
                                    checked={!(permissions.hiddenHomeSections || []).includes(section.id)}
                                    onChange={() => handleHomeSectionToggle(section.id)}
                                />
                                <span>
                                    <strong>{section.label}</strong>
                                    <small>{section.description}</small>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="permission-section">
                    <h3>Card Access & Actions</h3>
                    <p className="help-text">Select cards and optionally define specific actions per card</p>
                    
                    <div className="cards-list">
                        {AVAILABLE_CARDS.map(card => (
                            <div key={card.id} className="card-permission-item">
                                <label className="card-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={permissions.cards.includes(card.id)}
                                        onChange={() => handleCardToggle(card.id)}
                                    />
                                    <strong>{card.label}</strong>
                                </label>
                                
                                {permissions.cards.includes(card.id) && (
                                    <div className="card-actions">
                                        <span className="card-actions-label">Specific actions:</span>
                                        {AVAILABLE_ACTIONS.map(action => (
                                            <label key={action} className="checkbox-label-small">
                                                <input
                                                    type="checkbox"
                                                    checked={(permissions.cardActions[card.id] || []).includes(action)}
                                                    onChange={() => handleCardActionToggle(card.id, action)}
                                                />
                                                {action}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-actions">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-primary" 
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Permissions'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PermissionEditor;
